import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingCard = ({ plan, isPopular = false, onSelectPlan }) => {
  const formatPrice = (price) => {
    if (price === 0) return 'Free';
    if (typeof price === 'string') return price;
    return `$${price?.toLocaleString()}`;
  };

  const formatFeature = (feature) => {
    if (typeof feature === 'string') return feature;
    return feature?.text;
  };

  const isFeatureIncluded = (feature) => {
    if (typeof feature === 'string') return true;
    return feature?.included !== false;
  };

  const getFeatureIcon = (feature) => {
    if (typeof feature === 'string') return 'Check';
    return feature?.included === false ? 'X' : 'Check';
  };

  const getFeatureColor = (feature) => {
    if (typeof feature === 'string') return 'text-accent';
    return feature?.included === false ? 'text-muted-foreground' : 'text-accent';
  };

  return (
    <div className={`relative bg-card border rounded-xl p-6 transition-all duration-200 hover:shadow-elevation-3 ${
      isPopular ? 'border-primary shadow-elevation-2 scale-105' : 'border-border hover:border-primary/50'
    }`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
          <div className="bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
            Most Popular
          </div>
        </div>
      )}
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-foreground mb-2">{plan?.name}</h3>
        <div className="mb-2">
          <span className="text-3xl font-bold text-foreground">{formatPrice(plan?.price)}</span>
          {plan?.price !== 0 && typeof plan?.price !== 'string' && (
            <span className="text-muted-foreground ml-1">/month</span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{plan?.description}</p>
        {plan?.annualDiscount && (
          <div className="mt-2 text-sm text-accent">
            Save {plan?.annualDiscount}% with annual billing
          </div>
        )}
      </div>
      <div className="space-y-3 mb-8">
        {plan?.features?.map((feature, index) => (
          <div key={index} className="flex items-start space-x-3">
            <Icon 
              name={getFeatureIcon(feature)} 
              size={16} 
              className={`mt-0.5 flex-shrink-0 ${getFeatureColor(feature)}`}
            />
            <span className={`text-sm ${
              isFeatureIncluded(feature) ? 'text-foreground' : 'text-muted-foreground line-through'
            }`}>
              {formatFeature(feature)}
            </span>
          </div>
        ))}
      </div>
      <div className="space-y-3">
        {plan?.name === 'enterprise' ? (
          <Button
            variant="outline"
            fullWidth
            iconName="MessageSquare"
            iconPosition="left"
            onClick={() => window.location.href = '/contact-sales'}
            className="hover:bg-primary hover:text-primary-foreground transition-colors"
          >
            Contact Sales
          </Button>
        ) : (
          <>
            <Button
              variant={isPopular ? 'default' : 'outline'}
              fullWidth
              iconName="ArrowRight"
              iconPosition="right"
              onClick={() => onSelectPlan(plan)}
              className={isPopular ? 'shadow-elevation-2' : ''}
            >
              {plan?.hasActiveInvoice ? 'Payment' : (plan?.ctaText || 'Get Started')}
            </Button>
            
            {plan?.hasDemo && (
              <Button
                variant="ghost"
                size="sm"
                fullWidth
                iconName="Play"
                iconPosition="left"
                className="text-muted-foreground hover:text-foreground"
              >
                Request Demo
              </Button>
            )}
          </>
        )}
      </div>
      {plan?.badge && (
        <div className="mt-4 text-center">
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent">
            <Icon name="Star" size={12} className="mr-1" />
            {plan?.badge}
          </span>
        </div>
      )}
    </div>
  );
};

export default PricingCard;